// src/components/devices/SmartLockDetails.js
import React, { useState, useContext, useRef, useEffect, useCallback, useMemo } from 'react';
import { AppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { toast as sonnerToast } from '../../lib/toast';
import LockControl from './LockControl';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Lock,
  Unlock,
  Wifi,
  WifiOff,
  AlertTriangle,
  Trash2,
  RotateCcw,
  Plus,
  MoreHorizontal,
  Eye,
  EyeOff,
  Check,
  X,
  GripHorizontal,
  Fingerprint,
  CreditCard,
  Calendar,
  Clock,
  Repeat,
  KeyRound,
  Key,
  Smartphone,
  Radio,
  DoorOpen,
  History
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { isLockedOf } from '../../lib/lockState';

const SmartLockDetails = ({ lock, onClose }) => {
  const {
    smartLocks,
    setAutoLockTime,
    setLockSound,
    addPasscode,
    deletePasscode,
    addFingerprint,
    deleteFingerprint,
    addCard,
    deleteCard,
    lockSmartLock,
    unlockSmartLock,
    refreshSmartLocks,
    deleteSmartLock,
    resetSmartLock,
    fetchLockLogs,
    refreshLockLogs,
    lockAccessLogEvents
  } = useContext(AppContext);

  const { isAdmin } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'locking' | 'unlocking' | null
  const expectedLockedRef = useRef(null); // boolean | null
  const actionTimeoutRef = useRef(null);

  const clearPendingAction = () => {
    setPendingAction(null);
    expectedLockedRef.current = null;
    setIsLoading(false);
    if (actionTimeoutRef.current) {
      clearTimeout(actionTimeoutRef.current);
      actionTimeoutRef.current = null;
    }
  };

  const [showAddAccess, setShowAddAccess] = useState(false);
  const [showEnrollment, setShowEnrollment] = useState(false);
  const [enrollmentType, setEnrollmentType] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [credentialToDelete, setCredentialToDelete] = useState(null);

  const [revealedCodes, setRevealedCodes] = useState({});

  // Get fresh lock data from smartLocks array (updates via WebSocket)
  const currentLock = smartLocks.find(l => l.lockMac === lock.lockMac);

  // Extract credentials and settings from current lock
  const credentials = {
    passcodes: currentLock?.credentials?.passcodes || [],
    fingerprints: currentLock?.credentials?.fingerprints || [],
    cards: currentLock?.credentials?.cards || []
  };

  const autoLockAppliedSeconds = currentLock?.settings?.autoLockTime ?? 0;
  const soundEnabled = currentLock?.settings?.lockSound ?? true;

  // Draft vs Applied state for Apple-like UX
  const [autoLockEnabled, setAutoLockEnabled] = useState(autoLockAppliedSeconds > 0);
  const [autoLockDraft, setAutoLockDraft] = useState(
    autoLockAppliedSeconds > 0 ? autoLockAppliedSeconds.toString() : ''
  );
  const [autoLockSaving, setAutoLockSaving] = useState(false);
  const autoLockTimeoutRef = useRef(null);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  // Sync with WebSocket updates (don't overwrite while saving)
  useEffect(() => {
    if (autoLockSaving) return;

    setAutoLockEnabled(autoLockAppliedSeconds > 0);
    setAutoLockDraft(autoLockAppliedSeconds > 0 ? String(autoLockAppliedSeconds) : '');
  }, [autoLockAppliedSeconds, autoLockSaving]);

  // End processing when WebSocket state matches expected
  useEffect(() => {
    if (!currentLock) return;
    const expected = expectedLockedRef.current;
    if (expected === null) return;

    const nowLocked = isLockedOf(currentLock);
    if (nowLocked === expected) {
      clearPendingAction();
    }
  }, [isLockedOf(currentLock)]);

  // Apple-style auto-lock toggle (immediate save)
  const handleAutoLockToggle = async (enabled) => {
    setAutoLockEnabled(enabled);

    if (!enabled) {
      // Toggle OFF - disable auto-lock immediately
      setAutoLockDraft('');
      setAutoLockSaving(true);
      try {
        await setAutoLockTime(lock.lockMac, 0);
        setAutoLockDraft('');
      } catch (error) {
        console.error('Error disabling auto-lock:', error);
        // Revert on error
        setAutoLockEnabled(true);
        showToast('Failed to disable auto-lock', 'error');
      } finally {
        setAutoLockSaving(false);
      }
    } else {
      // Toggle ON - set default and save immediately
      const defaultSeconds = 30;
      setAutoLockDraft(defaultSeconds.toString());
      setAutoLockSaving(true);
      try {
        await setAutoLockTime(lock.lockMac, defaultSeconds);
        setAutoLockDraft(defaultSeconds.toString());
      } catch (error) {
        console.error('Error enabling auto-lock:', error);
        // Revert on error
        setAutoLockEnabled(false);
        setAutoLockDraft('');
        showToast('Failed to enable auto-lock', 'error');
      } finally {
        setAutoLockSaving(false);
      }
    }
  };

  // Auto-lock draft change with debounce
  const handleAutoLockDraftChange = (value) => {
    setAutoLockDraft(value);

    // Don't disable on empty - let user clear and retype
    if (value === '') {
      return; // Just clear the draft, keep enabled state
    }

    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 5 && numValue <= 300) {
      // Valid - enable and debounce save
      setAutoLockEnabled(true);

      // Clear previous timeout
      if (autoLockTimeoutRef.current) {
        clearTimeout(autoLockTimeoutRef.current);
      }

      // Debounced save
      autoLockTimeoutRef.current = setTimeout(async () => {
        // Only save if changed
        if (numValue !== autoLockAppliedSeconds) {
          setAutoLockSaving(true);
          try {
            await setAutoLockTime(lock.lockMac, numValue);
          } catch (error) {
            console.error('Error saving auto-lock time:', error);
            // Revert on error
            setAutoLockDraft(
              autoLockAppliedSeconds > 0 ? autoLockAppliedSeconds.toString() : ''
            );
            if (autoLockAppliedSeconds === 0) {
              setAutoLockEnabled(false);
            }
            showToast('Failed to save auto-lock time', 'error');
          } finally {
            setAutoLockSaving(false);
          }
        }
      }, 500);
    }
  };

  // Flush save on blur/Enter
  const handleAutoLockDraftSubmit = async () => {
    // Clear any pending debounced save to prevent race
    if (autoLockTimeoutRef.current) {
      clearTimeout(autoLockTimeoutRef.current);
      autoLockTimeoutRef.current = null;
    }

    const value = autoLockDraft;
    if (value === '') {
      // Empty on submit = disable auto-lock
      setAutoLockEnabled(false);
      setAutoLockSaving(true);
      try {
        await setAutoLockTime(lock.lockMac, 0);
      } catch (error) {
        console.error('Error disabling auto-lock:', error);
        showToast('Failed to save', 'error');
      } finally {
        setAutoLockSaving(false);
      }
      return;
    }

    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 5 && numValue <= 300) {
      setAutoLockSaving(true);
      try {
        await setAutoLockTime(lock.lockMac, numValue);
      } catch (error) {
        console.error('Error saving auto-lock time:', error);
        // Revert on error
        setAutoLockDraft(
          autoLockAppliedSeconds > 0 ? autoLockAppliedSeconds.toString() : ''
        );
        if (autoLockAppliedSeconds === 0) {
          setAutoLockEnabled(false);
        }
        showToast('Failed to save', 'error');
      } finally {
        setAutoLockSaving(false);
      }
    }
  };

  const handleToggle = async () => {
    if (!currentLock) return;
    if (isLoading || pendingAction) return;

    const wasLocked = isLockedOf(currentLock);
    const nextLocked = !wasLocked;

    expectedLockedRef.current = nextLocked;
    setPendingAction(nextLocked ? 'locking' : 'unlocking');
    setIsLoading(true);

    if (actionTimeoutRef.current) clearTimeout(actionTimeoutRef.current);
    actionTimeoutRef.current = setTimeout(() => {
      setPendingAction(null);
      expectedLockedRef.current = null;
      setIsLoading(false);
      showToast('Taking longer than usual…', 'error');
    }, 8000);

    try {
      if (wasLocked) {
        await unlockSmartLock(lock.lockMac);
      } else {
        await lockSmartLock(lock.lockMac);
      }
      // NOTE: we do NOT clear loading here. We clear when websocket updates (or timeout).
    } catch (error) {
      console.error('Error toggling lock:', error);
      clearPendingAction();
      showToast('Failed to toggle lock', 'error');
    }
  };

  const handleUpdateSound = async (enabled) => {
    try {
      await setLockSound(lock.lockMac, enabled);
    } catch (error) {
      console.error('Error updating sound:', error);
      showToast('Failed to update lock sound', 'error');
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (autoLockTimeoutRef.current) {
        clearTimeout(autoLockTimeoutRef.current);
      }
      if (actionTimeoutRef.current) {
        clearTimeout(actionTimeoutRef.current);
      }
    };
  }, []);

  const convertToTTLockFormat = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}${month}${day}${hours}${minutes}`;
  };

  const handleDeleteCredential = async (type, id) => {
    if (!window.confirm('Remove this credential?')) return;
    try {
      setIsLoading(true);
      if (type === 'passcode') {
        await deletePasscode(lock.lockMac, id);
      } else if (type === 'fingerprint') {
        await deleteFingerprint(lock.lockMac, id);
      } else if (type === 'card') {
        await deleteCard(lock.lockMac, id);
      }
      await refreshSmartLocks();
      showToast('Credential removed');
    } catch (error) {
      console.error('Error deleting credential:', error);
      showToast('Failed to remove credential', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetLock = async () => {
    if (!window.confirm('Reset lock to factory settings? All credentials will be removed. This cannot be undone.')) {
      return;
    }
    try {
      setIsLoading(true);
      await resetSmartLock(lock.lockMac);
      onClose();
      showToast('Lock reset complete');
    } catch (error) {
      console.error('Error resetting lock:', error);
      showToast('Failed to reset lock', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteLock = async () => {
    if (!window.confirm('Delete lock from hub? This will remove the lock from the system storage only (not the physical lock).')) {
      return;
    }
    try {
      setIsLoading(true);
      await deleteSmartLock(lock.lockMac);
      onClose();
      showToast('Lock removed from hub');
    } catch (error) {
      console.error('Error deleting lock:', error);
      showToast('Failed to delete lock', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCodeReveal = (id) => {
    setRevealedCodes(prev => {
      const isCurrentlyRevealed = !!prev[id];
      return {
        ...prev,
        [id]: !isCurrentlyRevealed
      };
    });
  };

  const startEnrollment = (type) => {
    setEnrollmentType(type);
    setShowEnrollment(true);
  };

  const handleDeleteCredentialClick = (credential) => {
    setCredentialToDelete(credential);
  };

  const confirmDeleteCredential = async () => {
    if (!credentialToDelete) return;

    try {
      await handleDeleteCredential(credentialToDelete.type, credentialToDelete.id);
      setCredentialToDelete(null);
    } catch (error) {
      console.error('Error deleting credential:', error);
    }
  };

  const isOnline = currentLock.availabilityStatus === 'AVAILABLE' || currentLock.isConnected;
  const battery = currentLock.batteryLevel !== undefined ? currentLock.batteryLevel : currentLock.battery;

  const getStatusDot = (credential) => {
    const now = new Date();
    const validFrom = credential.validFrom ? new Date(credential.validFrom) : null;
    const validUntil = credential.validUntil ? new Date(credential.validUntil) : null;
    const isActive = credential.isActive !== false;

    if (!isActive) return { color: 'bg-orange-500', label: 'Paused' };
    if (validUntil && now > validUntil) return { color: 'bg-muted-foreground/30', label: 'Expired' };
    if (validFrom && now < validFrom) return { color: 'bg-blue-500', label: 'Upcoming' };
    return { color: 'bg-green-500', label: 'Active' };
  };

  const getAllCredentials = () => {
    const all = [
      ...credentials.passcodes.map(p => ({ ...p, type: 'passcode' })),
      ...credentials.fingerprints.map(f => ({ ...f, type: 'fingerprint' })),
      ...credentials.cards.map(c => ({ ...c, type: 'card' }))
    ];
    return all.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  };

  const renderCredentialIcon = (type) => {
    switch (type) {
      case 'fingerprint':
        return <Fingerprint className="h-4 w-4" />;
      case 'card':
        return <CreditCard className="h-4 w-4" />;
      default:
        return <Lock className="h-4 w-4" />;
    }
  };

  const renderCredentialList = () => {
    const allCredentials = getAllCredentials();

    if (allCredentials.length === 0) {
      return (
        <div className="text-center py-12">
          <Lock className="h-10 w-10 mx-auto text-muted-foreground/20 mb-3" />
          <p className="text-sm text-muted-foreground">No access credentials</p>
        </div>
      );
    }

    return (
      <div className="divide-y rounded-xl border bg-background/60">
        {allCredentials.map((credential) => {
          const status = getStatusDot(credential);
          return (
            <motion.div
              key={`${credential.type}-${credential.id}`}
              className="flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-2 h-2 rounded-full ${status.color}`} />
                <div className="text-muted-foreground">
                  {renderCredentialIcon(credential.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{credential.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {credential.type === 'passcode' && 'Passcode'}
                    {credential.type === 'fingerprint' && 'Fingerprint'}
                    {credential.type === 'card' && 'Card'}
                    {' • '}
                    {credential.mode === 'permanent' && 'Permanent'}
                    {credential.mode === 'oneTime' && 'One-time'}
                    {credential.mode === 'temporary' && 'Temporary'}
                    {credential.mode === 'recurring' && 'Recurring'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {credential.type === 'passcode' && (
                  <>
                    <code className="text-xs font-mono text-muted-foreground">
                      {revealedCodes[credential.id] ? credential.passcode : '••••••'}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => toggleCodeReveal(credential.id)}
                    >
                      {revealedCodes[credential.id] ? (
                        <EyeOff className="h-3 w-3" />
                      ) : (
                        <Eye className="h-3 w-3" />
                      )}
                    </Button>
                  </>
                )}
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => handleDeleteCredentialClick(credential)}
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl h-[90vh] p-0 overflow-hidden">
          <DialogTitle className="sr-only">
            {currentLock.lockAlias || currentLock.lockName || `Lock (${lock.lockMac})`}
          </DialogTitle>
          <div className="h-full flex flex-col">
            <div className="bg-background/80 backdrop-blur-xl">
              <div className="flex flex-col items-center pt-8 pb-4 px-6">
                <div className="absolute top-3 left-1/2 -translate-x-1/2">
                  <GripHorizontal className="h-5 w-5 text-muted-foreground/50" />
                </div>

                <div className="p-4 rounded-2xl bg-primary/5 mb-4">
                  {isLockedOf(currentLock) ? (
                    <Lock className="h-8 w-8 text-primary" />
                  ) : (
                    <Unlock className="h-8 w-8 text-red-500" />
                  )}
                </div>

                <h2 className="text-xl font-semibold mb-1">
                  {currentLock.lockAlias || currentLock.lockName || `Lock (${lock.lockMac})`}
                </h2>

                <p className="text-sm text-muted-foreground">
                  {isOnline ? (
                    <>
                      {pendingAction
                        ? pendingAction === 'unlocking' ? 'Unlocking…' : 'Locking…'
                        : isLockedOf(currentLock) ? 'Locked' : 'Unlocked'}
                      {battery !== undefined && ` • ${battery}%`}
                    </>
                  ) : (
                    'Offline • Last seen just now'
                  )}
                </p>
              </div>

              {isOnline && (
                <div className="flex justify-center pb-6">
                  <motion.div
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  >
                    <LockControl
                      isLocked={isLockedOf(currentLock)}
                      onLock={handleToggle}
                      onUnlock={handleToggle}
                      disabled={isLoading || !!pendingAction}
                      processing={pendingAction}
                      className="w-28 h-28"
                    />
                  </motion.div>
                </div>
              )}
            </div>

            <div className="flex-1 min-h-0 bg-background">
              <Tabs defaultValue="access" className="h-full min-h-0 flex flex-col">

                <TabsList className="shrink-0 w-[calc(100%-3rem)] h-10 bg-muted/60 mx-6 mt-6">
                  <TabsTrigger
                    value="access"
                    className="data-[state=active]:bg-background data-[state=active]:shadow-sm"
                  >
                    Access
                  </TabsTrigger>
                  <TabsTrigger
                    value="activity"
                    className="data-[state=active]:bg-background data-[state=active]:shadow-sm"
                  >
                    Activity
                  </TabsTrigger>
                  <TabsTrigger
                    value="info"
                    className="data-[state=active]:bg-background data-[state=active]:shadow-sm"
                  >
                    Info
                  </TabsTrigger>
                  <TabsTrigger
                    value="settings"
                    className="data-[state=active]:bg-background data-[state=active]:shadow-sm"
                  >
                    Settings
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="access" className="flex-1 min-h-0 overflow-hidden px-6 pb-6">
                  <div className="h-full flex flex-col">
                    <div className="shrink-0 flex items-center justify-between mb-3 mt-4">
                      <h3 className="text-sm font-medium text-muted-foreground">Access Credentials</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowAddAccess(true)}
                        className="h-8 text-primary"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>

                    <div
                      className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
                      style={{
                        WebkitOverflowScrolling: 'touch',
                        paddingRight: '0.25rem',
                        paddingBottom: '2.5rem'
                      }}
                    >
                      {renderCredentialList()}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="activity" className="flex-1 min-h-0 overflow-hidden px-6 pb-6">
                  <LockActivity
                    lockMac={lock.lockMac}
                    fetchLockLogs={fetchLockLogs}
                    refreshLockLogs={refreshLockLogs}
                    logEvent={lockAccessLogEvents[lock.lockMac]}
                  />
                </TabsContent>

                <TabsContent value="info" className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
                  <div className="space-y-1 pt-4">
                    <div className="flex items-center justify-between p-3">
                      <span className="text-sm text-muted-foreground">MAC Address</span>
                      <span className="text-sm font-mono">{currentLock.lockMac}</span>
                    </div>
                    <div className="flex items-center justify-between p-3">
                      <span className="text-sm text-muted-foreground">Battery</span>
                      <span className="text-sm">{battery !== undefined ? `${battery}%` : 'Unknown'}</span>
                    </div>
                    <div className="flex items-center justify-between p-3">
                      <span className="text-sm text-muted-foreground">Connection</span>
                      <span className="text-sm flex items-center gap-1">
                        {isOnline ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                        {isOnline ? 'Connected' : 'Disconnected'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3">
                      <span className="text-sm text-muted-foreground">Auto Lock</span>
                      <span className="text-sm">
                        {autoLockAppliedSeconds > 0 ? `${autoLockAppliedSeconds}s` : 'Off'}
                      </span>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="settings" className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
                  <div className="space-y-1 pt-4">
                    {/* Auto-Lock Timer - Apple Home Style */}
                    <div className="flex items-center justify-between p-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium">Auto-Lock Timer</p>
                        <p className="text-xs text-muted-foreground">
                          {autoLockEnabled
                            ? `Automatically locks after ${autoLockAppliedSeconds} seconds`
                            : 'Auto-lock is disabled'}
                        </p>
                      </div>
                      <Switch
                        checked={autoLockEnabled}
                        onCheckedChange={handleAutoLockToggle}
                      />
                    </div>

                    {/* Auto-Lock Timer Input - Only when enabled */}
                    {autoLockEnabled && (
                      <div className="flex items-center justify-between p-3 pt-0">
                        <div className="flex-1">
                          <p className="text-sm font-medium">Timer (seconds)</p>
                          <p className="text-xs text-muted-foreground">
                            Range: 5-300 seconds
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {autoLockSaving && (
                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          )}
                          <Input
                            type="number"
                            min="5"
                            max="300"
                            value={autoLockDraft}
                            onChange={(e) => handleAutoLockDraftChange(e.target.value)}
                            onBlur={handleAutoLockDraftSubmit}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleAutoLockDraftSubmit();
                                e.currentTarget.blur();
                              }
                            }}
                            className="w-24 h-10 text-right pr-2"
                            placeholder="30"
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between p-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium">Lock Sound</p>
                        <p className="text-xs text-muted-foreground">
                          Play sound when locking/unlocking
                        </p>
                      </div>
                      <Switch
                        checked={soundEnabled}
                        onCheckedChange={handleUpdateSound}
                      />
                    </div>
                  </div>

                  {isAdmin() && (
                    <>
                      <div className="pt-6">
                        <div className="h-px bg-border mb-4 mx-3" />
                        <div className="px-3">
                          <h3 className="text-sm font-semibold text-destructive mb-3">Danger Zone</h3>
                          <div className="space-y-2">
                            <div className="p-3 rounded-lg border border-orange-200">
                              <p className="text-xs text-muted-foreground mb-2">
                                Remove lock from hub storage only
                              </p>
                              <Button
                                variant="outline"
                                size="sm"
                                className="w-full border-orange-500 text-orange-600 hover:bg-orange-50"
                                onClick={handleDeleteLock}
                                disabled={isLoading}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Lock
                              </Button>
                            </div>

                            <div className="p-3 rounded-lg border border-destructive/50">
                              <p className="text-xs text-muted-foreground mb-2">
                                Factory reset. Removes all credentials permanently
                              </p>
                              <Button
                                variant="destructive"
                                size="sm"
                                className="w-full"
                                onClick={handleResetLock}
                                disabled={isLoading}
                              >
                                <RotateCcw className="h-4 w-4 mr-2" />
                                Reset Lock
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AnimatePresence>
        {showAddAccess && (
          <AddAccessDialog
            onClose={() => setShowAddAccess(false)}
            onSelectPasscode={() => {
              startEnrollment('passcode');
              setShowAddAccess(false);
            }}
            onSelectFingerprint={() => {
              startEnrollment('fingerprint');
              setShowAddAccess(false);
            }}
            onSelectCard={() => {
              startEnrollment('card');
              setShowAddAccess(false);
            }}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEnrollment && enrollmentType && (
          <EnrollmentDialog
            type={enrollmentType}
            onClose={() => setShowEnrollment(false)}
            lock={lock}
            setParentLoading={(loading) => setIsLoading(loading)}
            convertToTTLockFormat={convertToTTLockFormat}
            showToast={showToast}
            refreshSmartLocks={refreshSmartLocks}
            addPasscode={addPasscode}
            addFingerprint={addFingerprint}
            addCard={addCard}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {credentialToDelete && (
          <Dialog open={!!credentialToDelete} onOpenChange={() => setCredentialToDelete(null)}>
            <DialogContent className="max-w-sm rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
              <DialogTitle className="sr-only">Delete Credential</DialogTitle>
              <div className="bg-background/80 backdrop-blur-xl">
                <div className="flex flex-col items-center pt-8 pb-4 px-6">
                  <div className="p-4 rounded-2xl bg-destructive/10 mb-4">
                    <AlertTriangle className="h-8 w-8 text-destructive" />
                  </div>
                  <h2 className="text-lg font-semibold mb-1">Delete Credential</h2>
                  <p className="text-xs text-muted-foreground text-center">
                    Are you sure you want to delete this {credentialToDelete.type}?
                  </p>
                </div>
              </div>

              <div className="bg-background p-6 space-y-3">
                <p className="text-sm text-center">
                  <span className="font-medium">{credentialToDelete.name}</span>
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 h-10"
                    onClick={() => setCredentialToDelete(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1 h-10"
                    onClick={confirmDeleteCredential}
                    disabled={isLoading}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
          >
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg ${
              toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
            }`}>
              {toast.type === 'success' ? (
                <Check className="h-4 w-4" />
              ) : (
                <X className="h-4 w-4" />
              )}
              <span className="text-sm font-medium">{toast.message}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SmartLockDetails;

const ACTIVITY_METHOD_ICONS = {
  passcode: KeyRound,
  card: CreditCard,
  fingerprint: Fingerprint,
  app: Smartphone,
  key: Key,
  sensor: DoorOpen,
  remote: Radio
};

function formatSyncedRelative(iso) {
  if (!iso) return null;
  const diffMs = Date.now() - new Date(iso).getTime();
  if (isNaN(diffMs)) return null;
  if (diffMs < 60 * 1000) return 'just now';
  const mins = Math.floor(diffMs / (60 * 1000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatDayLabel(date) {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
}

function LockActivity({ lockMac, fetchLockLogs, refreshLockLogs, logEvent }) {
  const [records, setRecords] = useState([]);
  const [syncedAt, setSyncedAt] = useState(null);
  const [isFetching, setIsFetching] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const seenEventVersionRef = useRef(logEvent?.version || 0);

  const loadLogs = useCallback(async () => {
    try {
      setLoadFailed(false);
      const result = await fetchLockLogs(lockMac, { limit: 50 });
      setRecords(result?.records || []);
      setSyncedAt(result?.syncedAt || null);
    } catch (error) {
      setLoadFailed(true);
    } finally {
      setIsFetching(false);
    }
  }, [fetchLockLogs, lockMac]);

  // Fetch on tab open (component mounts when the tab becomes active)
  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Prepend new records arriving over WebSocket
  useEffect(() => {
    if (!logEvent || logEvent.version === seenEventVersionRef.current) return;
    seenEventVersionRef.current = logEvent.version;

    const incoming = logEvent.newRecords || [];
    if (incoming.length === 0) return;

    setRecords(prev => {
      const existingIds = new Set(prev.map(r => r.id));
      const fresh = incoming.filter(r => !existingIds.has(r.id));
      if (fresh.length === 0) return prev;
      return [...fresh, ...prev].sort((a, b) => new Date(b.ts) - new Date(a.ts));
    });
    setSyncedAt(new Date().toISOString());
  }, [logEvent]);

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refreshLockLogs(lockMac);
      await loadLogs();
    } catch (error) {
      sonnerToast.error('Could not refresh activity');
    } finally {
      setIsRefreshing(false);
    }
  };

  const dayGroups = useMemo(() => {
    const sorted = [...records].sort((a, b) => new Date(b.ts) - new Date(a.ts));
    const groups = [];
    const byKey = new Map();
    for (const record of sorted) {
      const date = new Date(record.ts);
      const key = date.toDateString();
      if (!byKey.has(key)) {
        const group = { key, label: formatDayLabel(date), records: [] };
        byKey.set(key, group);
        groups.push(group);
      }
      byKey.get(key).records.push(record);
    }
    return groups;
  }, [records]);

  const syncedLabel = formatSyncedRelative(syncedAt);

  const renderRow = (record) => {
    const failed = record.category === 'FAILED';
    const Icon = ACTIVITY_METHOD_ICONS[record.method] || Lock;
    const primary = record.actorName || record.typeName;
    const secondary = record.actorName ? record.typeName : record.identifier;
    const time = new Date(record.ts).toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    return (
      <div key={record.id} className="flex items-center gap-3 p-3">
        <div
          className={`p-1.5 rounded-full shrink-0 ${
            failed ? 'bg-destructive/10 text-destructive' : 'bg-primary/5 text-muted-foreground'
          }`}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium truncate ${failed ? 'text-destructive' : ''}`}>
            {primary}
          </p>
          {secondary && (
            <p className={`text-xs truncate ${failed ? 'text-destructive/70' : 'text-muted-foreground'}`}>
              {secondary}
            </p>
          )}
        </div>
        <span className="text-xs text-muted-foreground tabular-nums shrink-0">{time}</span>
      </div>
    );
  };

  const renderBody = () => {
    if (isFetching) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (loadFailed) {
      return (
        <div className="text-center py-12">
          <History className="h-10 w-10 mx-auto text-muted-foreground/20 mb-3" />
          <p className="text-sm text-muted-foreground mb-3">Couldn't load activity</p>
          <Button variant="ghost" size="sm" className="h-8 text-primary" onClick={loadLogs}>
            Try again
          </Button>
        </div>
      );
    }

    if (records.length === 0) {
      return (
        <div className="text-center py-12">
          <History className="h-10 w-10 mx-auto text-muted-foreground/20 mb-3" />
          <p className="text-sm text-muted-foreground">No activity yet</p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            Activity appears after the lock reports events
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {dayGroups.map(group => (
          <div key={group.key}>
            <h4 className="text-xs font-medium text-muted-foreground px-1 mb-1.5">
              {group.label}
            </h4>
            <div className="divide-y rounded-xl border bg-background/60">
              {group.records.map(renderRow)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="shrink-0 flex items-center justify-between mb-3 mt-4">
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-muted-foreground">Activity</h3>
          {syncedLabel && (
            <p className="text-xs text-muted-foreground/70">Synced {syncedLabel}</p>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="h-8 text-primary"
        >
          {isRefreshing ? (
            <>
              <div className="w-4 h-4 mr-1 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              Syncing…
            </>
          ) : (
            <>
              <RotateCcw className="h-4 w-4 mr-1" />
              Refresh
            </>
          )}
        </Button>
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
        style={{
          WebkitOverflowScrolling: 'touch',
          paddingRight: '0.25rem',
          paddingBottom: '2.5rem'
        }}
      >
        {renderBody()}
      </div>
    </div>
  );
}

function AddAccessDialog({ onClose, onSelectPasscode, onSelectFingerprint, onSelectCard }) {
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
        <DialogTitle className="sr-only">Add Access</DialogTitle>
        <div className="bg-background/80 backdrop-blur-xl">
          <div className="flex flex-col items-center pt-8 pb-4 px-6">
            <div className="absolute top-2 left-1/2 -translate-x-1/2">
              <GripHorizontal className="h-5 w-5 text-muted-foreground/50" />
            </div>
            <h2 className="text-lg font-semibold mb-1">Add Access</h2>
            <p className="text-xs text-muted-foreground">Choose credential type</p>
          </div>
        </div>

        <div className="bg-background p-6 space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <Button
              variant="outline"
              className="h-24 flex-col gap-2"
              onClick={onSelectPasscode}
            >
              <Lock className="h-6 w-6" />
              <span className="text-xs">Passcode</span>
            </Button>
            <Button
              variant="outline"
              className="h-24 flex-col gap-2"
              onClick={onSelectFingerprint}
            >
              <Fingerprint className="h-6 w-6" />
              <span className="text-xs">Fingerprint</span>
            </Button>
            <Button
              variant="outline"
              className="h-24 flex-col gap-2"
              onClick={onSelectCard}
            >
              <CreditCard className="h-6 w-6" />
              <span className="text-xs">Card</span>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EnrollmentDialog({
  type,
  onClose,
  lock,
  setParentLoading,
  convertToTTLockFormat,
  showToast,
  refreshSmartLocks,
  addPasscode,
  addFingerprint,
  addCard
}) {
  const [name, setName] = useState('');
  const [passcode, setPasscode] = useState('');
  const [mode, setMode] = useState('permanent');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [weekDays, setWeekDays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const weekDaysList = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' }
  ];

  const resetForm = () => {
    setName('');
    setPasscode('');
    setMode('permanent');
    setStartDate('');
    setEndDate('');
    setStartTime('09:00');
    setEndTime('17:00');
    setWeekDays([]);
  };

  const toggleWeekDay = (day) => {
    setWeekDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day]
    );
  };

  const getSchedulePreview = () => {
    switch (mode) {
      case 'permanent':
        return 'Always valid';
      case 'oneTime':
        return startDate ? `Valid for 6 hours from ${new Date(startDate).toLocaleString()}` : 'Valid for 6 hours from now';
      case 'temporary':
        if (startDate && endDate) {
          return `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`;
        }
        return 'Select date range';
      case 'recurring':
        if (weekDays.length === 0) return 'Select days of week';
        const days = weekDays.map(d => weekDaysList.find(wd => wd.value === d)?.label).join(', ');
        return `Every ${days}, ${startTime} - ${endTime}`;
      default:
        return '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (type === 'passcode' && (!passcode.trim() || !name.trim())) return;
    if ((type === 'fingerprint' || type === 'card') && !name.trim()) return;

    try {
      setIsLoading(true);
      setParentLoading(true);

      const credentialData = {
        name: name,
        type: mode
      };

      if (mode === 'recurring') {
        if (weekDays.length === 0) {
          showToast('Please select at least one day of the week', 'error');
          setIsLoading(false);
          setParentLoading(false);
          return;
        }
        credentialData.recurringConfig = { weekDays };
      }

      if (startDate) {
        credentialData.startDate = convertToTTLockFormat(startDate);
      }
      if (endDate) {
        credentialData.endDate = convertToTTLockFormat(endDate);
      }

      let result;
      if (type === 'passcode') {
        result = await addPasscode(lock.lockMac, passcode, credentialData.name, credentialData.startDate, credentialData.endDate, credentialData.type, credentialData.recurringConfig);
      } else if (type === 'fingerprint') {
        result = await addFingerprint(lock.lockMac, credentialData.name, credentialData.startDate, credentialData.endDate, credentialData.recurringConfig);
      } else if (type === 'card') {
        result = await addCard(lock.lockMac, credentialData.name, credentialData.startDate, credentialData.endDate, credentialData.recurringConfig);
      }

      console.log(`${type} add result:`, result);

      if (result?.success && result?.lock) {
        resetForm();
        onClose();

        setIsLoading(false);
        setParentLoading(false);

        showToast(`${type === 'passcode' ? 'Passcode' : type.charAt(0).toUpperCase() + type.slice(1)} added successfully`);
      } else {
        throw new Error(result?.message || 'Failed to add credential');
      }
    } catch (error) {
      console.error(`Error adding ${type}:`, error);

      setIsLoading(false);
      setParentLoading(false);

      const errorMessage = error?.response?.data?.message || error?.message || `Failed to add ${type}`;
      showToast(errorMessage, 'error');
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'fingerprint':
        return 'Add Fingerprint';
      case 'card':
        return 'Add Card';
      default:
        return 'Add Passcode';
    }
  };

  const getDescription = () => {
    switch (type) {
      case 'fingerprint':
        return 'Place your finger on the lock sensor';
      case 'card':
        return 'Tap your card against the lock';
      default:
        return '';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'fingerprint':
        return <Fingerprint className="h-8 w-8 text-primary" />;
      case 'card':
        return <CreditCard className="h-8 w-8 text-primary" />;
      default:
        return null;
    }
  };

  const getButtonText = () => {
    switch (type) {
      case 'fingerprint':
        return 'Enroll Fingerprint';
      case 'card':
        return 'Enroll Card';
      default:
        return 'Add Passcode';
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-sm rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
        <DialogTitle className="sr-only">{getTitle()}</DialogTitle>
        <div className="bg-background/80 backdrop-blur-xl">
          <div className="flex flex-col items-center pt-8 pb-4 px-6">
            <div className="absolute top-2 left-1/2 -translate-x-1/2">
              <GripHorizontal className="h-5 w-5 text-muted-foreground/50" />
            </div>
            {getIcon() && (
              <div className="p-4 rounded-2xl bg-primary/10 mb-4">
                {getIcon()}
              </div>
            )}
            <h2 className="text-lg font-semibold mb-1">{getTitle()}</h2>
            {getDescription() && (
              <p className="text-xs text-muted-foreground text-center">
                {getDescription()}
              </p>
            )}
          </div>
        </div>

        <div className="bg-background p-6 space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm">Name</Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={type === 'passcode' ? 'e.g. Guest, Cleaner' : type === 'fingerprint' ? 'e.g. Right Thumb' : 'e.g. Work Card'}
                className="h-10"
                disabled={isLoading}
                required
              />
            </div>

            {type === 'passcode' && (
              <div className="space-y-2">
                <Label className="text-sm">Passcode</Label>
                <Input
                  type="text"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value)}
                  placeholder="6-8 digits"
                  pattern="[0-9]{6,8}"
                  className="h-10"
                  disabled={isLoading}
                  required
                />
              </div>
            )}

            <div className="space-y-3 pt-2">
              <Label className="text-sm">Mode</Label>
              <ModeSelector mode={mode} onChange={setMode} type={type} disabled={isLoading} />
            </div>

            {mode === 'recurring' && (
              <>
                <div className="space-y-2">
                  <Label className="text-sm">Days of Week</Label>
                  <WeekDaySelector weekDays={weekDays} onToggle={toggleWeekDay} weekDaysList={weekDaysList} />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Time Range</Label>
                  <TimeRangePicker startTime={startTime} endTime={endTime} onStartChange={setStartTime} onEndChange={setEndTime} />
                </div>
              </>
            )}

            {mode === 'temporary' && (
              <div className="space-y-2">
                <Label className="text-sm">Date Range (Required)</Label>
                <DateRangePicker startDate={startDate} endDate={endDate} onStartChange={setStartDate} onEndChange={setEndDate} />
              </div>
            )}

            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">
                <span className="font-medium">Preview:</span> {getSchedulePreview()}
              </p>
            </div>

            <Button type="submit" className="w-full h-10" disabled={isLoading}>
              {getButtonText()}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ModeSelector({ mode, onChange, type }) {
  const getModeButtons = () => {
    if (type === 'passcode') {
      return [
        { value: 'permanent', label: 'Permanent', icon: Lock },
        { value: 'oneTime', label: 'One-time', icon: Clock },
        { value: 'temporary', label: 'Temporary', icon: Calendar },
        { value: 'recurring', label: 'Recurring', icon: Repeat }
      ];
    }
    return [
      { value: 'permanent', label: 'Permanent', icon: Lock },
      { value: 'temporary', label: 'Temporary', icon: Calendar },
      { value: 'recurring', label: 'Recurring', icon: Repeat }
    ];
  };

  const buttons = getModeButtons();

  return (
    <div className="grid grid-cols-2 gap-2">
      {buttons.map(({ value, label, icon: Icon }) => (
        <Button
          key={value}
          type="button"
          variant={mode === value ? 'default' : 'outline'}
          size="sm"
          onClick={() => onChange(value)}
          className="h-9"
        >
          <Icon className="h-4 w-4 mr-1" />
          {label}
        </Button>
      ))}
    </div>
  );
}

function WeekDaySelector({ weekDays, onToggle, weekDaysList }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {weekDaysList.map(day => (
        <Button
          key={day.value}
          type="button"
          variant={weekDays.includes(day.value) ? 'default' : 'outline'}
          size="sm"
          onClick={() => onToggle(day.value)}
          className="h-8 px-3"
        >
          {day.label}
        </Button>
      ))}
    </div>
  );
}

function TimeRangePicker({ startTime, endTime, onStartChange, onEndChange }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label className="text-xs text-muted-foreground">Start Time</Label>
        <Input
          type="time"
          value={startTime}
          onChange={(e) => onStartChange(e.target.value)}
          className="h-8"
          required
        />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">End Time</Label>
        <Input
          type="time"
          value={endTime}
          onChange={(e) => onEndChange(e.target.value)}
          className="h-8"
          required
        />
      </div>
    </div>
  );
}

function DateRangePicker({ startDate, endDate, onStartChange, onEndChange }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label className="text-xs text-muted-foreground">Start</Label>
        <Input
          type="date"
          value={startDate}
          onChange={(e) => onStartChange(e.target.value)}
          className="h-8"
          required
        />
      </div>
      <div>
        <Label className="text-xs text-muted-foreground">End</Label>
        <Input
          type="date"
          value={endDate}
          onChange={(e) => onEndChange(e.target.value)}
          className="h-8"
          required
        />
      </div>
    </div>
  );
}
